import React, { useState, useEffect, useRef } from "react";
import { RadioField, Field, TextField, LongText, InfoPanel, InlineCheck } from "./fields";
import { FUNDING_OPTIONS } from "../../lib/options";
import { Button } from "../ui/button";
import { INTAKE } from "../../constants/testIds";
import { sendUrgentContact } from "../../lib/api";
import { dobError, postcodeError } from "../../lib/validation";
import { toast } from "sonner";
import { Phone, ShieldAlert } from "lucide-react";
import { NO_FUNDING, NoFundingOptions, CloseButton, NoticePanel } from "./NoFundingNotice";

const OPTIONS = [
  { value: "danger", label: "Someone is in immediate danger." },
  { value: "child_removal", label: "A child may be removed from England and Wales." },
  { value: "hearing_soon", label: "A hearing is taking place within two working days." },
  { value: "deadline_soon", label: "A court deadline expires within two working days." },
  { value: "none", label: "None of these applies." },
];


const EmergencyPanel = () => (
  <InfoPanel tone="danger" testid={INTAKE.emergencyPanel}>
    <p className="font-serif text-lg mb-1 text-brand-danger">If someone is in immediate danger</p>
    <p>
      If you or someone else is in immediate danger, call <strong>999</strong> now. If you cannot speak,
      call 999, then press 55 when prompted. For non-emergencies you can call the police on 101.
    </p>
    <p className="mt-2">
      You can also contact the 24-hour National Domestic Abuse Helpline on{" "}
      <strong>0808 2000 247</strong>. Use the <strong>Quick exit</strong> button at any time to leave this
      page immediately.
    </p>
  </InfoPanel>
);

const UrgentContactForm = ({ enquiryId, reason, onDone, onCapture, initial }) => {
  const [f, setF] = useState({
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    email: initial?.email || "",
    phone: initial?.mobile || "",
    dob: "",
    postcode: "",
    budget: "",
    summary: "",
    ack: false,
    no_funding_choice: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const noticeRef = useRef(null);
  const noFunding = f.budget === NO_FUNDING;
  useEffect(() => {
    if (noFunding && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [noFunding]);
  // Carry contact details up to the main form as they are typed, so they persist
  // even if the user switches to "None of these applies" without sending the urgent request.
  useEffect(() => {
    if (onCapture) onCapture(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.first_name, f.last_name, f.email, f.phone, f.dob, f.postcode]);
  const dobErr = dobError(f.dob);
  const postcodeErr = postcodeError(f.postcode);
  const contactDone = f.dob && !dobErr && f.postcode && !postcodeErr;
  const summaryDone = !!(f.summary && f.summary.trim());
  const nfChoice = f.no_funding_choice;
  const showClose = noFunding && nfChoice === "none";
  const valid = f.first_name && f.last_name && (f.email || f.phone) && f.dob && !dobErr && f.postcode && !postcodeErr && f.summary && f.ack && f.budget && (!noFunding || nfChoice === "secure");

  const submit = async () => {
    setSending(true);
    try {
      const res = await sendUrgentContact({ ...f, subject: "Urgent enquiry", reason, enquiry_id: enquiryId });
      toast.success(`Urgent request sent (ref ${res.reference}). We have acknowledged it by email.`);
      if (onCapture) onCapture(f);
      onDone();
    } catch (e) {
      toast.error("Could not send the urgent request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 border border-brand-gold/60 bg-white p-6" data-testid={INTAKE.urgentForm}>
      <div className="flex items-center gap-2 text-brand-black">
        <Phone className="h-5 w-5 text-brand-gold" strokeWidth={1.5} />
        <h3 className="font-serif text-xl">Urgent contact request</h3>
      </div>
      <p className="text-sm text-brand-darkgrey">
        Please give us a few details and we will treat this as urgent. This does not confirm that we can
        assist or that a barrister has been booked.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="First name"><TextField value={f.first_name} onChange={(v) => set("first_name", v)} testid="urgent-first-name" /></Field>
        <Field label="Last name"><TextField value={f.last_name} onChange={(v) => set("last_name", v)} testid="urgent-last-name" /></Field>
        <Field label="Email address"><TextField type="email" value={f.email} onChange={(v) => set("email", v)} testid="urgent-email" /></Field>
        <Field label="Telephone number"><TextField value={f.phone} onChange={(v) => set("phone", v)} testid="urgent-phone" /></Field>
      </div>
      <p className="text-xs text-brand-darkgrey -mt-1" data-testid="urgent-contact-hint">Please provide at least an email address or a telephone number so we can reach you.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Date of birth" required>
          <TextField type="date" value={f.dob} onChange={(v) => set("dob", v)} testid="urgent-dob" />
          {dobErr && <p className="text-xs text-brand-danger mt-1" data-testid="urgent-dob-error">{dobErr}</p>}
        </Field>
        <Field label="Postcode" required>
          <TextField value={f.postcode} onChange={(v) => set("postcode", v)} testid="urgent-postcode" />
          {postcodeErr && <p className="text-xs text-brand-danger mt-1" data-testid="urgent-postcode-error">{postcodeErr}</p>}
        </Field>
      </div>
      {contactDone && (<>
      <Field label="Subject"><TextField value="Urgent enquiry" onChange={() => {}} disabled testid="urgent-subject" /></Field>
      <Field label="Short summary of the situation and why it is urgent" required>
        <LongText value={f.summary} onChange={(v) => set("summary", v)} testid="urgent-summary" />
        {!(f.summary && f.summary.trim()) && (
          <p className="text-xs text-brand-danger mt-1" data-testid="urgent-summary-hint">
            Please complete this field before sending your urgent request.
          </p>
        )}
      </Field>
      </>)}
      <div ref={noticeRef}>
        <NoticePanel noFunding={noFunding} testid="urgent-funding-notice" />
      </div>
      {noFunding && (
        <NoFundingOptions value={nfChoice} onChange={(v) => set("no_funding_choice", v)} prefix="urgent" />
      )}
      {contactDone && (
      <div className="border border-border bg-white p-4" data-testid="urgent-funding-ack-box">
        <InlineCheck
          checked={f.ack}
          onChange={(v) => set("ack", !!v)}
          label="I understand that this is a privately funded service."
          testid="urgent-funding-ack"
          disabled={!summaryDone}
        />
      </div>
      )}
      {contactDone && f.ack && (
        <Field label="What budget do you presently have available?" required>
          <RadioField options={FUNDING_OPTIONS} value={f.budget} onChange={(v) => set("budget", v)} name="urgent-budget" testid="urgent-budget" />
        </Field>
      )}
      <div className="flex flex-wrap gap-3">
        {showClose ? (
          <CloseButton testid="urgent-close" source="urgent" />
        ) : (
          <Button
            onClick={submit}
            disabled={!valid || sending}
            data-testid={INTAKE.urgentSubmit}
            className="bg-brand-danger text-white hover:brightness-110 rounded-sm"
          >
            {sending ? "Sending…" : "Send urgent request"}
          </Button>
        )}
      </div>
    </div>
  );
};

export const Stage1Triage = ({ form, set, enquiryId, onContinue }) => {
  const [sent, setSent] = useState(false);
  const value = form.triage?.option || "";
  const setOption = (v) => {
    set("triage", { ...(form.triage || {}), option: v });
    if (v === "none") {
      window.setTimeout(() => onContinue(), 220);
    }
  };

  useEffect(() => {
    setSent(false);
  }, [value]);

  const isDanger = value === "danger";
  const isUrgent = ["child_removal", "hearing_soon", "deadline_soon"].includes(value);

  const disclaimerRef = useRef(null);
  useEffect(() => {
    if (!(isDanger || isUrgent)) return undefined;
    const id = setTimeout(() => {
      const el = disclaimerRef.current;
      if (!el || typeof window === "undefined") return;
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 160);
    return () => clearTimeout(id);
  }, [value, isDanger, isUrgent]);

  // Carry the urgent-form details into the main form so the contact step is pre-filled.
  const captureContact = (f) => {
    set("contact", {
      ...(form.contact || {}),
      first_name: f.first_name,
      last_name: f.last_name,
      email: f.email,
      mobile: f.phone,
      dob: f.dob,
      address: f.postcode,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl sm:text-4xl text-brand-black">Getting you the right help</h2>
        <p className="mt-3 text-sm text-brand-darkgrey leading-relaxed max-w-2xl">
          This form asks a few questions so that we can understand your enquiry and direct it to the
          appropriate team. It usually takes approximately 5 minutes. You may save and return to the form
          if you need more time. Submitting the form does not confirm that we can assist with your case or
          that a barrister has been booked.
        </p>
      </div>

      <Field label="Does any of the following apply?">
        <RadioField options={OPTIONS} value={value} onChange={setOption} name="triage" testid={INTAKE.triageOption} />
      </Field>

      {isDanger && (
        <div className="space-y-4" ref={disclaimerRef}>
          <EmergencyPanel />
          {!sent && (
            <UrgentContactForm
              enquiryId={enquiryId}
              reason="danger"
              onDone={() => setSent(true)}
              onCapture={captureContact}
              initial={form.contact}
            />
          )}
          {sent && (
            <InfoPanel tone="gold">
              <p className="font-medium mb-3">Your urgent request has been sent, it would be very helpful if you could complete the full form.</p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={onContinue} data-testid={INTAKE.urgentContinue} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm border-2 border-brand-gold pulse-gold">
                  Continue to the full form
                </Button>
              </div>
            </InfoPanel>
          )}
        </div>
      )}

      {isUrgent && (
        <div className="space-y-4" ref={disclaimerRef}>
          {!sent && (
            <InfoPanel tone="danger">
              <ShieldAlert className="inline h-4 w-4 mr-1 -mt-0.5" strokeWidth={1.5} />
              Because your matter appears to be time-critical, please send us an urgent contact request below.
            </InfoPanel>
          )}
          {!sent ? (
            <UrgentContactForm enquiryId={enquiryId} reason={value} onDone={() => setSent(true)} onCapture={captureContact} initial={form.contact} />
          ) : (
            <InfoPanel tone="gold">
              <p className="font-medium mb-3">Your urgent request has been sent, it would be very helpful if you could complete the full form.</p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={onContinue} data-testid={INTAKE.urgentContinue} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm border-2 border-brand-gold pulse-gold">
                  Continue to the full form
                </Button>
              </div>
            </InfoPanel>
          )}
        </div>
      )}
    </div>
  );
};
